import React, { useState, useEffect, useMemo } from 'react';
import { FaPen } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';
import { BASE_URL } from '../../Services/Services';
import ClipLoader from 'react-spinners/ClipLoader';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

const PersonalInfo = () => {
  const { t } = useTranslation();
  const email = localStorage.getItem('email');
  const [isOtpAction, setIsOtpAction] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const initialFormData = useMemo(() => ({
    fullName: localStorage.getItem('fullName'),
    email,
    phoneNumber: localStorage.getItem('phoneNumber') || null,
    newEmail: null,
    profilePicture: null,
    newPhoneNumber: '',
    otp: '',
    isOtpSent: false,
    isOtpVerified: false,
    showModal: false,
  }), [email]);

  const [formData, setFormData] = useState(initialFormData);
  const [isEditable, setIsEditable] = useState({
    name: false,
    newEmail: false,
    profilePicture: false,
  });
  const [loading, setLoading] = useState(false);

  const profilePicture = localStorage.getItem('profilePictureUrl');

  useEffect(() => {
    setFormData(initialFormData);
  }, [initialFormData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    const updatedValue = name === 'newEmail' ? value.toLowerCase() : value;

    setFormData({
      ...formData,
      [name]: updatedValue,
    });
  };


  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setFormData({
        ...formData,
        profilePicture: file,
        imageUrl: imageUrl,
      });
    }
  };


  const hasChanges = () => {
    return (
      formData.fullName !== initialFormData.fullName ||
      formData.email !== initialFormData.email ||
      formData.newEmail !== initialFormData.newEmail ||
      formData.profilePicture !== null
    );
  };
  
  const handleEditClick = (field) => {
    setIsEditable({
      ...isEditable,
      [field]: !isEditable[field],
    });
  };

  const validateEmail = (email) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  };

  const validatePhoneNumber = (phoneNumber) => {
    const phonePattern = /^[+]?[0-9]{10,15}$/;
    return phonePattern.test(phoneNumber);
  };

  const handleLogout = async () => {
    localStorage.removeItem('fullName');
    localStorage.removeItem('email');
    localStorage.removeItem('userId');
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (isOtpAction) return;

    toast.dismiss();

    if (!hasChanges()) {
      toast.error('No changes detected. No need to update.');
      return;
    }

    if (!validateEmail(formData.email)) {
      toast.error('Current email is invalid.');
      return;
    }

    if (formData.newEmail && !validateEmail(formData.newEmail)) {
      toast.error('New email is invalid.');
      return;
    }

    if (formData.newEmail === email) {
      toast.error('Please use a different email; it matches the existing one');
      return;
    }

    setLoading(true);

    const dataToSend = new FormData();
    dataToSend.append('fullName', formData.fullName);
    dataToSend.append('email', formData.email);
    dataToSend.append('newEmail', formData.newEmail || '');
    if (formData.profilePicture) {
      dataToSend.append('profilePicture', formData.profilePicture);
    }

    axios.put(`${BASE_URL}/profileUpdate`, dataToSend, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    })
      .then(response => {
        if (response?.status === 200) {
          localStorage.setItem('profilePictureUrl', response.data.profilePictureUrl);
          setFormData(prevFormData => ({
            ...prevFormData,
            newEmail: '',
          }));

          toast.dismiss();
          if (formData.newEmail) {
            toast.success('A verification email has been sent to the new email address. Please check your inbox!');
            handleLogout();
          } else {
            toast.success('Profile updated successfully.');
          }
        }
      })
      .catch(error => {
        toast.dismiss();
        if (error?.response?.status === 500 || error?.response?.status === 503) {
          toast.error('Service is down. Try again later');
        } if (error?.response?.status === 400) {
          const errorMessage = error?.response?.data?.message || 'Bad Request. Please check the input.';
          toast.error(errorMessage);
        } else {
          toast.error(error?.response?.data?.message || 'Service is down! Try again later');
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // const handleUpdatePhoneNumber = () => {
  //   setFormData({
  //     ...formData,
  //     showModal: true,
  //   });
  // };

  const sendOtp = async () => {
    toast.dismiss();
    if (!validatePhoneNumber(formData.newPhoneNumber)) {
      toast.error('New phone number is invalid.');
      return;
    }


    try {
      setIsOtpAction(true);
      const response = await axios.post('http://localhost:9999/LOGINREGISTRATION/api/v1/phoneNumberUpdate', {
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        newPhoneNumber: formData.newPhoneNumber,
      });

      toast.dismiss();
      if (response.status === 200) {
        toast.success(response?.data || 'otp sent to mobile!');
        setFormData({
          ...formData,
          isOtpSent: true,
        });
      }
    } catch (error) {
      toast.dismiss();
      if (error?.response?.status === 400) {
        toast.error(error?.response?.data);
      }
      else {

        toast.error('Failed to send OTP');
      }
    }
    finally {
      setIsOtpAction(false);
    }
  };

  const verifyOtp = async () => {
    toast.dismiss();
    if (!formData.otp || formData.otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP.');
      return;
    }

    try {
      setIsOtpAction(true);
      const response = await axios.post('http://localhost:9999/LOGINREGISTRATION/api/v1/phoneNumberVerify', {
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        newPhoneNumber: formData.newPhoneNumber,
        otpCode: formData.otp,
      });

      toast.dismiss();
      if (response.status === 200) {
        toast.success(response?.data || 'Mobile number updated successfully!');
        setFormData({
          ...formData,
          isOtpVerified: true,
          showModal: false,
        });

        setShowLogoutModal(true);

        setTimeout(() => {
          localStorage.clear();
          window.location.reload();
        }, 5000);
      }
    } catch (error) {
      toast.dismiss();
      toast.error('OTP verification failed!');
    } finally {
      setIsOtpAction(false);
    }
  };


  const getProfileImageUrl = () => {
    if (formData?.imageUrl && typeof formData.imageUrl === 'string') {
      return formData.imageUrl;
    }

    if (profilePicture && typeof profilePicture === 'string' && profilePicture !== 'null' && profilePicture !== 'undefined') {
      return profilePicture;
    }

    return 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png';
  };

  return (
    <div className="section-content">
      <form onSubmit={handleSubmit}>
        <div className="image-container">
          <img src={getProfileImageUrl()} alt="Profile" width="100" />
          <label htmlFor="imageUpload">
            <FaPen style={{ cursor: 'pointer' }} color={'white'} />
          </label>
          <input
            id="imageUpload"
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            style={{ display: 'none' }}
          />
        </div>

        <div className="input-container-p">
          <input
            className="input-p"
            type="text"
            name="fullName"
            placeholder={t("FullName")}
            value={formData.fullName}
            onChange={handleInputChange}
            disabled={!isEditable.name}
          />
          <FaPen
            className="input-pen"
            style={{ cursor: 'pointer' }}
            onClick={() => handleEditClick('name')}
          />
        </div>

        <div className="input-container-p">
          <input
            className="input-p"
            type="email"
            name="email"
            placeholder={t("Email")}
            value={formData.email}
            onChange={handleInputChange}
            disabled={true}
          />
        </div>

        <div className="input-container-p">
          <input
            className="input-p"
            type="email"
            name="newEmail"
            placeholder={t("NewEmail")}
            value={formData.newEmail}
            onChange={handleInputChange}
            disabled={!isEditable.newEmail}
          />
          <FaPen
            className="input-pen"
            style={{ cursor: 'pointer' }}
            onClick={() => handleEditClick('newEmail')}
          />
        </div>

        {/* <div className="input-container-p">
          <input
            className="input-p"
            type="text"
            name="phoneNumber"
            placeholder="Phone number"
            value={formData?.phoneNumber}
            disabled={true}
          />
          <span
            className='update-mobile-button'
            onClick={handleUpdatePhoneNumber}
          >
            Update Phone number
          </span>
        </div> */}

        {formData.showModal && (
          <div className="overlay-mobile-update">
            <div className="modal">
              <div className='mobile-update-header-container'>
                <h3>Update Mobile Number</h3>
                <div onClick={() => setFormData({ ...formData, showModal: false })} className='close-x-button'>x</div>
              </div>

              <input
                className="input-p"
                type="text"
                name="newPhoneNumber"
                placeholder="Enter phone number with country code (e.g., +91)"
                value={formData.newPhoneNumber}
                onChange={handleInputChange}
              />
              <button onClick={sendOtp} className='submit-mobile-button'>Send OTP</button>
              {formData.isOtpSent && (
                <>
                  <input
                    className="input-p"
                    type="text"
                    name="otp"
                    placeholder="Enter OTP"
                    value={formData.otp}
                    onChange={handleInputChange}
                  />
                  <button onClick={verifyOtp} className='submit-mobile-button'>Update</button>
                </>
              )}
            </div>


          </div>
        )}


        <button type="submit" className="save-btn">{t("Save")}</button>

        {loading && (
          <div className="loading-overlay">
            <ClipLoader color="black" loading={loading} size={50} />
            <p>{t("Please wait, your profile is being updated.")}</p>
          </div>
        )}
      </form>

      {showLogoutModal && (
        <div className="overlay-logout-update">
          <div className="modal">
            <h4>We are logging you out. Please log in again with your updated credentials.</h4>
            <button onClick={() => {
              localStorage.clear();
              window.location.reload();
            }}>OK</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalInfo;
